import React, { useEffect, useState } from "react";
import { X, Clock, User, Bot, AlertCircle } from "lucide-react";
import { eventsApi } from "@/lib/api";
import { fmtTime, fmtDate } from "@/lib/format";
import clsx from "clsx";

const ICONS = {
  user: <User className="w-3 h-3 text-cyan" />,
  ai: <Bot className="w-3 h-3 text-amber" />,
  system: <AlertCircle className="w-3 h-3 text-ink-mute" />,
};

const ACTION_LABELS = {
  task_created: "Created",
  status_changed: "Status",
  locked: "Locked",
  unlocked: "Unlocked",
  block_created: "Scheduled",
  block_moved: "Moved",
  block_deleted: "Deleted",
  task_deleted: "Task deleted",
  blocker_added: "Blocker",
  ai_adjusted: "AI adjusted",
};

export default function CellHistoryDrawer({ open, onClose, block, task }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !task) return;
    setLoading(true);
    eventsApi.forTask(task.id, 100)
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  if (!open) return null;
  return (
    <div className="drawer animate-slide-in-right" data-testid="cell-history-drawer">
      <div className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-surface">
        <div>
          <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">CELL HISTORY</div>
          <div className="text-sm text-ink mt-0.5 truncate" title={task?.title}>{task?.title}</div>
        </div>
        <button onClick={onClose} className="btn-ghost" data-testid="history-close">
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        {loading && <div className="p-4 mono text-2xs text-ink-mute uppercase">Loading…</div>}
        {!loading && events.length === 0 && (
          <div className="p-4 mono text-2xs text-ink-mute uppercase tracking-widest">No events recorded yet.</div>
        )}
        <div className="divide-y divide-line">
          {events.map((e) => (
            <div key={e.id} className="px-3 py-2 hover:bg-bg-highlight transition-colors">
              <div className="flex items-center gap-2">
                {ICONS[e.actor_type] || ICONS.system}
                <span className="mono text-2xs uppercase tracking-wider text-amber">
                  {ACTION_LABELS[e.action] || e.action}
                </span>
                <span className="mono text-2xs text-ink-mute ml-auto tabular-nums">
                  {fmtTime(e.created_at)} · {fmtDate(e.created_at).split(" ").slice(0, 2).join(" ")}
                </span>
              </div>
              {Object.keys(e.payload || {}).length > 0 && (
                <pre className="mt-1 mono text-2xs text-ink-dim whitespace-pre-wrap break-all ml-5">
                  {JSON.stringify(e.payload, null, 0).slice(0, 280)}
                </pre>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="p-3 border-t border-line bg-bg-surface mono text-2xs uppercase tracking-widest text-ink-mute">
        {events.length} event{events.length === 1 ? "" : "s"}
      </div>
    </div>
  );
}
